import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ReceiptVoucherPrint.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com/api';
const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}${logoPath}`;
};

const ReceiptVoucherPrint = forwardRef(({ receiptVoucher, organization }, ref) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  if (!receiptVoucher) return null;

  // Get organization data or use defaults
  const orgNameAr = organization?.organizationNameAr || organization?.OrganizationNameAr || 'عقار لايف';
  const orgNameEng = organization?.organizationNameEng || organization?.OrganizationNameEng || 'Agar Live';
  const crNumber = organization?.cRNumber || organization?.CRNumber || '1010483226';
  const vatNumber = organization?.vATNumber || organization?.VATNumber || '';
  const streetName = organization?.streetName || organization?.StreetName || '';
  const district = organization?.district || organization?.District;
  const city = organization?.city || organization?.City;
  const districtNameAr = district?.districtNameAr || district?.DistrictNameAr || '';
  const districtNameEng = district?.districtNameEng || district?.DistrictNameEng || '';
  const cityNameAr = city?.cityNameAr || city?.CityNameAr || '';
  const cityNameEng = city?.cityNameEng || city?.CityNameEng || '';
  
  const address = organization 
    ? `${streetName}${districtNameAr || districtNameEng ? ` - ${districtNameAr || districtNameEng}` : ''}${cityNameAr || cityNameEng ? ` - ${cityNameAr || cityNameEng}` : ''}`
    : 'الرياض - حي الريان';
  const addressEng = organization
    ? `${streetName}${districtNameEng || districtNameAr ? ` - ${districtNameEng || districtNameAr}` : ''}${cityNameEng || cityNameAr ? ` - ${cityNameEng || cityNameAr}` : ''}`
    : 'Riyadh - Al-Rayyan district';
  const logoUrl = organization ? getLogoUrl(organization.logo || organization.Logo) : null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const isCash = receiptVoucher.paymentMethod === 'Cash' || receiptVoucher.paymentMethod === 'نقداً';
  const isCheque = receiptVoucher.paymentMethod === 'Cheque' || receiptVoucher.paymentMethod === 'شيك';

  return (
    <div className="voucher-print-container" ref={ref}>
      <div className="voucher-print-content">
        
        {/* Header with 3 columns */}
        <div className="voucher-header">
          
          {/* Right Side - Arabic */}
          <div className="voucher-header-right">
            <div className="company-name-ar">{orgNameAr}</div>
            <div className="company-detail">C.R. {crNumber}</div>
            {vatNumber && <div className="company-detail">VAT: {vatNumber}</div>}
            <div className="company-address">العنوان : {address}</div>
            
            {/* Simple text for date and amount */}
            <div className="date-amount-text">
              {formatDate(receiptVoucher.voucherDate)}    {formatAmount(receiptVoucher.amount)} ريال
            </div>
          </div>
          
          {/* Middle - Logo and Title */}
          <div className="voucher-header-center">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="voucher-logo-img"
                crossOrigin="anonymous"
                onError={(e) => {
                  // Hide image and show fallback on error
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            {!logoUrl && (
              <div className="voucher-logo">LOGO</div>
            )}
            <div className="voucher-title-ar">سند قبض</div>
            <div className="voucher-title-en">Receipt Voucher</div>
          </div>
          
          {/* Left Side - English */}
          <div className="voucher-header-left">
            <div className="company-name-en">{orgNameEng}</div>
            <div className="company-detail">C.R. {crNumber}</div>
            {vatNumber && <div className="company-detail">VAT: {vatNumber}</div>}
            <div className="company-address">Address: {addressEng}</div>
            <div className="voucher-number-container">
              <span className="voucher-number-label">No.</span>
              <span className="voucher-number-value">{receiptVoucher.voucherNumber || ''}</span>
            </div>
          </div>
          
        </div>
        
        {/* Received From Row */}
        <div className="voucher-row">
          <div className="voucher-label-ar">استلمنا من المكرم /</div>
          <div className="voucher-line"></div>
          <div className="voucher-value">{receiptVoucher.receivedFrom || ''}</div>
          <div className="voucher-label-en">RECEIVED FROM</div>
        </div>
        
        {/* Amount In Words Row */}
        <div className="voucher-row">
          <div className="voucher-label-ar">مبلغ وقدره</div>
          <div className="voucher-line"></div>
          <div className="voucher-value">{receiptVoucher.amountInWords || ''}</div>
          <div className="voucher-label-en">The sum</div>
        </div>
        
        {/* Payment Method Row */}
        <div className="voucher-payment-row">
          <input 
            type="checkbox" 
            className="voucher-checkbox" 
            checked={isCash}
            readOnly
          />
          <span className="voucher-checkbox-label">نقداً</span>
          
          <input 
            type="checkbox" 
            className="voucher-checkbox" 
            checked={isCheque}
            readOnly
          />
          <span className="voucher-checkbox-label">شيك رقم</span>
          
          <div className="voucher-line-inline">{receiptVoucher.chequeNumber || ''}</div>
          
          <span className="voucher-label-inline">على بنك</span>
          <div className="voucher-line-inline">{receiptVoucher.bank || ''}</div>
          
          <span className="voucher-label-inline">Bank</span>
          <div className="voucher-line-inline"></div>
          
          <span className="voucher-label-inline">Cheque No</span>
          <div className="voucher-line-inline"></div>
          
          <input 
            type="checkbox" 
            className="voucher-checkbox" 
            checked={isCash}
            readOnly
          />
          <span className="voucher-checkbox-label">Cash</span>
          
          <input 
            type="checkbox" 
            className="voucher-checkbox" 
            checked={isCheque}
            readOnly
          />
          <span className="voucher-checkbox-label">Cheque</span>
        </div>
        
        {/* Purpose Row */}
        <div className="voucher-row">
          <div className="voucher-label-ar">وذلك عن قيمة</div>
          <div className="voucher-line"></div>
          <div className="voucher-value">{receiptVoucher.purpose || ''}</div>
          <div className="voucher-label-en">For</div>
        </div>
        
        {/* Additional lines */}
        <div className="voucher-line-full"></div>
        <div className="voucher-line-full" style={{ marginTop: '25px' }}></div>
        
        {/* Signatures Row */}
        <div className="voucher-signatures">
          <div className="voucher-signature">
            <span className="signature-label">المستلم <span>Receiver</span></span>
            <div className="signature-line"></div>
          </div>
          
          <div className="voucher-signature">
            <span className="signature-label">المحاسب <span>Accountant</span></span>
            <div className="signature-line"></div>
          </div>
          
          <div className="voucher-signature">
            <span className="signature-label">المدير <span>Manager</span></span>
            <div className="signature-line"></div>
          </div>
        </div>
        
      </div>
    </div>
  );
});

ReceiptVoucherPrint.displayName = 'ReceiptVoucherPrint';

export default ReceiptVoucherPrint;

